/*
 * Native "ordered photos per variant" gallery.
 * Built to replace the Swatch King app's per-variant gallery behaviour with
 * an independent implementation driven entirely by the `ooc.gallery_media`
 * variant metafield (an ordered list of media set per variant).
 *
 * On page load, immediately when a variant is selected (read straight off
 * Dawn's variant-radios/variant-selects component), and again whenever Dawn
 * publishes its (network-driven, slightly delayed) variantChange event, this
 * filters + reorders the media gallery so the newly selected variant's
 * assigned photos appear first, in the order the merchant assigned them.
 * Photos not assigned to any variant are treated as shared/general photos
 * and always stay visible. Photos assigned to a *different* variant are
 * hidden.
 */
(function () {
  if (window.__oocVariantGalleryInit) return;
  window.__oocVariantGalleryInit = true;

  function parseMap(scriptEl) {
    if (!scriptEl) return {};
    try {
      return JSON.parse(scriptEl.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  function extractMediaId(value) {
    var match = String(value || '').match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  function collectAllListedIds(map) {
    var all = {};
    Object.keys(map).forEach(function (variantId) {
      (map[variantId] || []).forEach(function (id) {
        all[String(id)] = true;
      });
    });
    return all;
  }

  function reorderList(ul, ownOrder, exclusiveIds, isThumbnailList) {
    if (!ul) return;

    var items = Array.prototype.slice.call(ul.children);
    var byMediaId = {};
    items.forEach(function (li) {
      var raw = isThumbnailList ? li.dataset.target : li.dataset.mediaId;
      var mediaId = extractMediaId(raw);
      if (mediaId) byMediaId[mediaId] = li;
    });

    var used = {};
    var finalOrder = [];

    ownOrder.forEach(function (mediaId) {
      var li = byMediaId[mediaId];
      if (li && !used[mediaId]) {
        li.classList.remove('variant-gallery-hidden');
        finalOrder.push(li);
        used[mediaId] = true;
      }
    });

    items.forEach(function (li) {
      var raw = isThumbnailList ? li.dataset.target : li.dataset.mediaId;
      var mediaId = extractMediaId(raw);
      if (mediaId && used[mediaId]) return;

      if (mediaId && exclusiveIds[mediaId]) {
        li.classList.add('variant-gallery-hidden');
      } else {
        li.classList.remove('variant-gallery-hidden');
      }
      finalOrder.push(li);
    });

    ul.append.apply(ul, finalOrder);
  }

  // Dawn's <slider-component> (powers the prev/next arrows + "1 / N"
  // counter for both the main gallery and the thumbnail strip) captures its
  // slide list and pagination math once, then caches it. Reordering/hiding
  // slides afterwards leaves that cache stale, breaking the arrows and
  // counter. resetPages() is the same method Dawn itself calls after its
  // own native single-image swap, so we call it again after OUR reorder to
  // bring the cache back in sync with the real DOM.
  function refreshSlider(ul) {
    if (!ul) return;
    var sliderComponent = ul.closest('slider-component');
    if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
      sliderComponent.resetPages();
    }
  }

  // This theme also renders a completely separate mobile-only image
  // carousel (a jQuery/slick "sliderForMobile" component, `<ul
  // id="slider-{{ section.id }}">`) alongside the gallery above. It's
  // populated exactly once - by cloning every <li> out of the native
  // gallery's own list - the first time the page detects a narrow (<750px)
  // viewport (see product-media-gallery.liquid's own inline script). Those
  // cloned <li> elements are separate DOM nodes with no live connection
  // back to the native list, so toggling `variant-gallery-hidden` up above
  // (on every later variant switch) never reaches them: once the mobile
  // carousel exists, it stays frozen showing whichever variant's photos
  // happened to be visible at the moment it was first built, no matter how
  // many times the shopper changes variant afterwards. That's the "shows
  // too many photos at once" / stale-photos bug on mobile.
  //
  // Fix: whenever we finish re-filtering the native gallery for a variant
  // change, and the mobile carousel has already been built (it has cloned
  // <li> children) and slick has already taken it over, throw it away and
  // rebuild it from scratch using only the native gallery's current
  // (already-filtered, already-reordered) *visible* <li> elements. This is
  // the exact same clone approach product-media-gallery.liquid's own script
  // already uses for the very first build - just re-run on every variant
  // change instead of only once - so the mobile carousel's slide count,
  // dot count, and "X of Y" counter all stay correct, with no blank hidden
  // slides left swipeable in the middle of it.
  function syncMobileCarousel(galleryEl, mainList) {
    if (!galleryEl || !mainList) return;

    // Lowercase "slider-" on purpose: this only matches the mobile
    // carousel's `id="slider-{{ section.id }}"`, never the native gallery's
    // own `id="Slider-Gallery-..."` / `id="Slider-Thumbnails-..."` (both
    // capital "S"), since HTML id attribute matching here is case-sensitive.
    var mobileList = galleryEl.querySelector('ul[id^="slider-"]');
    if (!mobileList || !mobileList.children.length) {
      // Not built yet (still desktop-width, or hasn't loaded that far) -
      // nothing to keep in sync.
      return;
    }

    var $ = window.jQuery || window.$;
    if (!$ || !$.fn || !$.fn.slick) return;

    var $mobileList = $(mobileList);
    if (!$mobileList.hasClass('slick-initialized')) return;

    var visibleClones = [];
    Array.prototype.forEach.call(mainList.children, function (li) {
      if (!li.classList.contains('variant-gallery-hidden')) {
        visibleClones.push(li.cloneNode(true));
      }
    });
    if (!visibleClones.length) return;

    $mobileList.slick('unslick');
    mobileList.innerHTML = '';
    visibleClones.forEach(function (li) {
      mobileList.appendChild(li);
    });
    $mobileList.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      dots: true,
      infinite: false,
      autoplay: false,
    });
  }

  // This theme's desktop gallery layout ("thumbnail_slider") only lays out
  // the ONE currently-active slide - every other slide's <li> sits at
  // `display: none` - and Dawn marks every gallery image `loading="lazy"`.
  // Native lazy-loading never starts fetching an image that's inside a
  // `display: none` ancestor, so a slide's photo doesn't even begin
  // downloading until the instant its thumbnail is clicked and it becomes
  // active. On a fast/cached connection that's invisible; on an ordinary
  // connection it's exactly the glitch reported: the newly-active slide
  // briefly paints as a small, blurry, still-downloading image before
  // snapping to full size once the real bytes finish loading.
  //
  // Fix: as soon as a variant's own photos are known (on page load and on
  // every variant switch), flip those specific images from lazy to eager.
  // Per spec, `loading="eager"` starts the fetch immediately regardless of
  // visibility/display, so by the time a shopper actually clicks through to
  // photo 2 or 3 the browser already has it cached and there's nothing to
  // visibly "load in".
  function preloadOwnPhotos(mainList, ownOrder) {
    if (!mainList) return;
    var byMediaId = {};
    Array.prototype.forEach.call(mainList.children, function (li) {
      var mediaId = extractMediaId(li.dataset.mediaId);
      if (mediaId) byMediaId[mediaId] = li;
    });
    ownOrder.forEach(function (mediaId) {
      var li = byMediaId[mediaId];
      var img = li ? li.querySelector('img') : null;
      if (img && !img.complete && img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
      }
    });
  }

  function applyForGallery(galleryEl, variantId) {
    if (!galleryEl || !variantId) return;
    var mapScript = galleryEl.querySelector('[data-variant-gallery-map]');
    if (!mapScript) return;

    var map = parseMap(mapScript);
    var ownOrder = (map[String(variantId)] || []).map(String);

    // Nothing configured for this variant: leave the gallery untouched.
    if (!ownOrder.length) return;

    var allListed = collectAllListedIds(map);
    var ownSet = {};
    ownOrder.forEach(function (id) {
      ownSet[id] = true;
    });
    var exclusiveIds = {};
    Object.keys(allListed).forEach(function (id) {
      if (!ownSet[id]) exclusiveIds[id] = true;
    });

    var mainList = galleryEl.querySelector('ul[id^="Slider-Gallery-"]');
    var thumbList = galleryEl.querySelector('ul[id^="Slider-Thumbnails-"]');

    reorderList(mainList, ownOrder, exclusiveIds, false);
    reorderList(thumbList, ownOrder, exclusiveIds, true);

    refreshSlider(mainList);
    refreshSlider(thumbList);

    preloadOwnPhotos(mainList, ownOrder);
    syncMobileCarousel(galleryEl, mainList);
  }

  function applyForSection(sectionId, variantId) {
    if (!sectionId || !variantId) return;
    document
      .querySelectorAll('[id^="MediaGallery-' + sectionId + '"]')
      .forEach(function (galleryEl) {
        applyForGallery(galleryEl, variantId);
      });
  }

  function initGallery(galleryEl) {
    var mapScript = galleryEl.querySelector('[data-variant-gallery-map]');
    if (!mapScript) return;
    var initialVariantId = mapScript.getAttribute('data-initial-variant-id');
    if (initialVariantId) applyForGallery(galleryEl, initialVariantId);
  }

  // Dawn's own variant-radios/variant-selects "change" handler (bound
  // directly on that custom element, set up when the page first parses it)
  // runs synchronously as soon as a shopper picks a variant: it figures out
  // `this.currentVariant` and immediately does its OWN native single-image
  // swap (prepending that variant's single native "featured media" slide
  // and marking it active), all before it kicks off the network request
  // that eventually re-renders the section and publishes the `variantChange`
  // pubsub event we already listen for below.
  //
  // That native swap only prepends + toggles `.is-active` - it has no idea
  // about our `variant-gallery-hidden` class, so if the slide it just
  // prepended happens to already be hidden by us (because it belonged to
  // the previously-selected variant's "different variant" set), it stays
  // invisible. The shopper keeps seeing the OLD variant's photos until our
  // own reorder runs - and our reorder was only wired up to run once that
  // slower, network-driven `variantChange` event arrives, ~150-300ms later.
  // That gap is what produced the "loads, resizes, ends up smaller" glitch:
  // the visible photo didn't actually shrink, but the correct photo stayed
  // hidden for a beat while a differently-shaped older photo (and its
  // native active/scroll-into-view handling) sat on screen, then the whole
  // gallery visibly snapped over once the network response finally landed.
  //
  // Fix: listen for the same "change" event ourselves. Because our listener
  // is registered on `document` (an ancestor of variant-radios/selects), it
  // always fires during the bubble phase AFTER Dawn's own listener on the
  // component itself has already run - so `currentVariant` is already set
  // and Dawn's native swap has already happened, synchronously, in the same
  // tick as the click. We reorder immediately using that variant id, no
  // network round-trip needed. The later pubsub-driven reorder still runs
  // too (harmless - it's idempotent) as a safety net in case a variant ever
  // resolves differently between the two paths.
  function handleImmediateVariantChange(event) {
    var host = event.target.closest('variant-radios, variant-selects');
    if (!host) return;
    var variant = host.currentVariant;
    if (!variant || !variant.id) return;
    var sectionId = host.dataset.section || host.getAttribute('data-section');
    applyForSection(sectionId, String(variant.id));
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(initGallery);

    document.addEventListener('change', handleImmediateVariantChange);

    // Dawn's pubsub.js declares `subscribe` and `PUB_SUB_EVENTS` as plain
    // top-level bindings in a classic (non-module) script. `subscribe` ends
    // up on `window` because it's a function declaration, but `PUB_SUB_EVENTS`
    // is declared with `const`, so it is NOT reachable via `window.PUB_SUB_EVENTS`
    // - only as the bare identifier. Reference both as bare identifiers so this
    // keeps working regardless of how they were declared.
    var pubSubEvents;
    try {
      pubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
    } catch (error) {
      pubSubEvents = null;
    }

    if (typeof subscribe === 'function' && pubSubEvents && pubSubEvents.variantChange) {
      subscribe(pubSubEvents.variantChange, function (event) {
        if (!event || !event.data || !event.data.variant || !event.data.sectionId) return;
        applyForSection(event.data.sectionId, String(event.data.variant.id));
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
